import type { CircleLayerSpecification, SymbolLayerSpecification } from "maplibre-gl";

import { TREAT_COLORS } from "../lib/spot-offer.ts";

export const SPOTS_SOURCE_ID = "spots";

export const SPOT_LAYERS = {
  clusters: "clusters",
  clusterRing: "cluster-ring",
  clusterCount: "cluster-count",
  points: "points",
} as const;

/**
 * Pile de polices du serveur de glyphes du fond de carte. Coupler l'étiquette
 * des agrégats à la police du basemap est un compromis assumé : c'est ce qui
 * évite d'héberger un serveur de glyphes pour trois chiffres. Changer de
 * fournisseur de fond impose donc de vérifier que cette pile existe chez lui.
 */
const BASEMAP_FONT = ["noto_sans_bold"];

/** Un agrégat porte son compte et se dézoome au clic. */
export const clusterLayer: CircleLayerSpecification = {
  id: SPOT_LAYERS.clusters,
  type: "circle",
  source: SPOTS_SOURCE_ID,
  filter: ["has", "point_count"],
  paint: {
    "circle-color": TREAT_COLORS.available,
    "circle-opacity": 0.9,
    "circle-radius": [
      "interpolate",
      ["linear"],
      ["get", "point_count"],
      2,
      14,
      10,
      20,
      50,
      28,
      200,
      36,
    ],
    "circle-stroke-width": 2.5,
    "circle-stroke-color": "#150b1f",
  },
};

export const clusterRingLayer: CircleLayerSpecification = {
  id: SPOT_LAYERS.clusterRing,
  type: "circle",
  source: SPOTS_SOURCE_ID,
  filter: ["has", "point_count"],
  paint: {
    "circle-color": "transparent",
    "circle-radius": [
      "interpolate",
      ["linear"],
      ["get", "point_count"],
      2,
      20,
      10,
      26,
      50,
      34,
      200,
      42,
    ],
    "circle-stroke-width": 1,
    "circle-stroke-color": TREAT_COLORS.available,
    "circle-stroke-opacity": 0.4,
  },
};

/**
 * Les pastilles isolées sont des icônes, pas des ronds : la silhouette dit
 * l'état des bonbons, l'anneau dit la décoration. L'image est choisie par la
 * propriété `icon` calculée dans `to-geojson.ts`.
 *
 * `icon-allow-overlap` est nécessaire : sans lui MapLibre masque les symboles
 * qui se chevauchent, et deux maisons voisines feraient disparaître l'une des
 * deux au lieu de se superposer légèrement.
 */
export const pointLayer: SymbolLayerSpecification = {
  id: SPOT_LAYERS.points,
  type: "symbol",
  source: SPOTS_SOURCE_ID,
  filter: ["!", ["has", "point_count"]],
  layout: {
    "icon-image": ["get", "icon"],
    "icon-size": ["interpolate", ["linear"], ["zoom"], 11, 0.5, 15, 0.72, 19, 0.95],
    "icon-allow-overlap": true,
    "icon-ignore-placement": true,
    "icon-anchor": "center",
  },
};

/** Le nombre de maisons regroupées, écrit dans le disque. */
export const clusterCountLayer: SymbolLayerSpecification = {
  id: SPOT_LAYERS.clusterCount,
  type: "symbol",
  source: SPOTS_SOURCE_ID,
  filter: ["has", "point_count"],
  layout: {
    "text-field": ["get", "point_count_abbreviated"],
    "text-font": BASEMAP_FONT,
    "text-size": ["interpolate", ["linear"], ["get", "point_count"], 2, 13, 100, 17],
    "text-allow-overlap": true,
    "text-ignore-placement": true,
  },
  paint: {
    "text-color": "#150b1f",
  },
};
